import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { Invitation } from "../../services/invitationApi";
import { FaEnvelope, FaClock, FaCheck, FaTimes, FaRedo } from "react-icons/fa";

interface InvitationsListProps {
  invitations: Invitation[];
  loading: boolean;
  onResend: (id: string) => Promise<void>;
  onCancel: (id: string) => Promise<void>;
  resendingId?: string;
  cancellingId?: string;
}

export const InvitationsList: React.FC<InvitationsListProps> = ({
  invitations,
  loading,
  onResend,
  onCancel,
  resendingId,
  cancellingId,
}) => {
  const { t } = useTranslation();

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "accepted":
        return "bg-green-100 text-green-800";
      case "expired":
        return "bg-gray-100 text-gray-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status: string): React.ReactNode => {
    switch (status) {
      case "pending":
        return <FaClock className="text-sm" />;
      case "accepted":
        return <FaCheck className="text-sm" />;
      case "expired":
      case "cancelled":
        return <FaTimes className="text-sm" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="lg" />
      </div>
    );
  }

  if (invitations.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {t("admin.invitations.noInvitations")}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {t("admin.invitations.email")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {t("admin.invitations.status")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {t("admin.invitations.invitedBy")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {t("admin.invitations.sentAt")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {t("admin.invitations.expiresAt")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {t("admin.actions")}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {invitations.map((invitation) => (
            <tr key={invitation._id}>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center gap-2">
                  <FaEnvelope className="text-gray-400" />
                  {invitation.email}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 py-1 text-xs font-semibold flex items-center gap-1 w-fit ${getStatusColor(
                    invitation.status
                  )}`}
                >
                  {getStatusIcon(invitation.status)}
                  {t(`admin.invitations.statusLabels.${invitation.status}`)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {invitation.invitedBy?.name || "-"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {new Date(invitation.createdAt).toLocaleDateString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {invitation.expiresAt
                  ? new Date(invitation.expiresAt).toLocaleDateString()
                  : "-"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <div className="flex gap-2">
                  {invitation.status === "pending" && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onResend(invitation._id)}
                        disabled={resendingId === invitation._id}
                      >
                        {resendingId === invitation._id ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                            <FaRedo className="mr-1" />
                            {t("admin.invitations.resend")}
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (window.confirm(t("admin.invitations.confirmCancel", { email: invitation.email }))) {
                            onCancel(invitation._id);
                          }
                        }}
                        disabled={cancellingId === invitation._id}
                        className="text-red-600 hover:text-red-700"
                      >
                        {cancellingId === invitation._id ? (
                          <Spinner size="sm" />
                        ) : (
                          t("admin.invitations.cancel")
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
